import { CalendarClock, GripVertical, MessageSquareText, TriangleAlert, Users } from "lucide-react";
import { Gauge } from "@/components/ComplianceGauge";
import { fmtDay, fmtLakhValue } from "@/lib/api";

const BANDS = {
  on_track: "hsl(var(--healthy))",
  needs_attention: "hsl(var(--watch))",
  delayed: "hsl(var(--at-risk))",
};
const band = (v) =>
  v === null || v === undefined
    ? "hsl(var(--muted-foreground))"
    : v >= 95
      ? BANDS.on_track
      : v >= 80
        ? BANDS.needs_attention
        : BANDS.delayed;

/** One of the three compliance columns — a bar that reads N/A rather than nought when a day has
 *  not been filled, because nobody filling it is not the same as nought per cent. */
const Bar = ({ label, value, testId }) => {
  const has = value !== null && value !== undefined;
  const height = has ? Math.max(4, Math.min(100, Number(value))) : 0;
  return (
    <div className="flex w-[52px] flex-col items-center" data-testid={testId}>
      <span
        className="font-data text-[13px] font-bold tabular-nums"
        style={{ color: has ? band(value) : "hsl(var(--muted-foreground))" }}
      >
        {has ? `${Math.round(value)}%` : "N/A"}
      </span>
      <span className="relative mt-1 flex h-[88px] w-7 items-end bg-border/70">
        <span
          className="w-full transition-[height] duration-700 ease-out"
          style={{ height: `${height}%`, backgroundColor: band(value) }}
        />
      </span>
      <span className="font-data mt-1 text-[8.5px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
    </div>
  );
};

/** Target, baseline and where the KPI actually stands, on one band. */
const Bullet = ({ kpi }) => {
  const { target, baseline, actual } = kpi;
  const ready = [target, baseline].every((v) => v !== null && v !== undefined) && target !== baseline;
  const lo = ready ? Math.min(target, baseline) : 0;
  const hi = ready ? Math.max(target, baseline) : 1;
  const span = hi - lo || 1;
  const at = (v) =>
    v === null || v === undefined
      ? null
      : `${Math.max(0, Math.min(100, ((v - lo) / span) * 100))}%`;
  // The band runs from the promise to where it started — good is always the target end.
  const targetLeft = ready && target <= baseline;

  return (
    <div className="min-w-[180px]">
      <div className="label-caps">KPI</div>
      <div className="mt-1 truncate text-[13px] font-semibold leading-snug" title={kpi.name}>
        {kpi.name || "not named"}
      </div>
      <div className="font-data text-[10px] text-muted-foreground">{kpi.unit || "—"}</div>

      {!ready ? (
        <p className="mt-3 flex items-start gap-1.5 text-[10px] leading-snug text-[hsl(var(--watch))]">
          <TriangleAlert className="mt-px h-3 w-3 shrink-0" />
          Target and baseline are still to be declared, so there is no scale to read the KPI on.
        </p>
      ) : (
        <div className="mt-4">
          <div className="flex justify-between">
            <span className="font-data text-[9px] uppercase tracking-wider text-muted-foreground">
              {targetLeft ? "Target" : "Baseline"}
            </span>
            <span className="font-data text-[9px] uppercase tracking-wider text-muted-foreground">
              {targetLeft ? "Baseline" : "Target"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-data text-[12px] font-bold tabular-nums">
              {targetLeft ? target : baseline}
            </span>
            <span className="font-data text-[12px] font-bold tabular-nums">
              {targetLeft ? baseline : target}
            </span>
          </div>
          <div
            className="relative mt-1 h-[11px] w-full"
            style={{
              background: targetLeft
                ? "linear-gradient(90deg, hsl(var(--healthy)) 0%, hsl(var(--watch)) 55%, hsl(var(--at-risk)) 100%)"
                : "linear-gradient(90deg, hsl(var(--at-risk)) 0%, hsl(var(--watch)) 45%, hsl(var(--healthy)) 100%)",
            }}
          >
            <span className="absolute inset-y-[-3px] left-0 w-[2px] bg-foreground" />
            <span className="absolute inset-y-[-3px] right-0 w-[2px] bg-foreground" />
            {at(actual) !== null && (
              <span
                className="absolute -bottom-[7px] -translate-x-1/2"
                style={{ left: at(actual) }}
              >
                <span
                  className="block h-0 w-0 border-x-[5px] border-t-[7px] border-x-transparent"
                  style={{ borderTopColor: "hsl(var(--foreground))" }}
                />
              </span>
            )}
          </div>
          <div className="mt-1.5 flex items-baseline justify-between gap-2">
            <span
              className="font-data text-[13px] font-black tabular-nums"
              style={{ color: band(kpi.journey_pct) }}
            >
              {actual ?? "no reading"}
            </span>
            <span className="font-data text-[9px] text-muted-foreground">
              {kpi.journey_pct !== null && kpi.journey_pct !== undefined
                ? `${Math.round(kpi.journey_pct)}% of the way`
                : "actual KPI yet to be filled"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * One usecase's odometer, read left to right: who and what · the three compliance bars · the KPI
 * on its band · target against actual impact · when it was last filled.
 *
 * Every figure comes from the portal's one compliance store. Pressing the card opens the trend.
 */
export const OdometerCard = ({
  card,
  onOpen,
  draggable,
  dragging,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => (
  <div
    data-testid={`odometer-${card.code}`}
    draggable={draggable}
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDrop={onDrop}
    onDragEnd={onDragEnd}
    className={`group relative bg-card transition-all duration-200 ${
      dragging ? "opacity-40" : "hover:bg-muted/40"
    }`}
  >
    <span
      className="absolute inset-y-0 left-0 w-[3px] transition-opacity duration-300"
      style={{ backgroundColor: band(card.compliance.cumulative_pct) }}
    />
    {draggable && (
      <span
        className="absolute left-1 top-1/2 -translate-y-1/2 cursor-grab text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100 active:cursor-grabbing"
        data-testid={`odometer-grip-${card.code}`}
        title="Drag to rearrange"
      >
        <GripVertical className="h-4 w-4" />
      </span>
    )}

    <button
      type="button"
      data-testid={`odometer-open-${card.code}`}
      onClick={() => onOpen?.(card.project_id)}
      className="grid w-full grid-cols-1 items-start gap-5 px-6 py-5 text-left lg:grid-cols-[minmax(230px,1.5fr)_auto_minmax(190px,1.1fr)_auto_minmax(130px,0.8fr)] lg:gap-0 lg:divide-x lg:divide-border"
    >
      {/* who and what */}
      <span className="block lg:pr-6">
        <span className="font-data flex flex-wrap items-center gap-1.5 text-[9.5px] uppercase tracking-wider">
          <span className="border border-border px-1.5 py-0.5 text-muted-foreground">
            {card.plant}
          </span>
          <span className="bg-primary px-1.5 py-0.5 text-primary-foreground">
            {card.phase || card.status || "—"}
          </span>
          <span className="text-muted-foreground">{card.code}</span>
        </span>
        <span className="mt-1.5 block font-display text-[19px] font-black leading-[1.15] tracking-tight">
          {card.title}
        </span>
        {card.responsible.length > 0 && (
          <span className="mt-1.5 flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
            <Users className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="line-clamp-2">{card.responsible.join(", ")}</span>
          </span>
        )}
        <span className="mt-1.5 block text-[10px] text-muted-foreground">
          Assessment from {fmtDay(card.assessment_from)}
          {card.dept_shop ? ` · ${card.dept_shop}` : ""}
        </span>
      </span>

      {/* the three bars */}
      <span className="block lg:px-6">
        <span className="label-caps block">Compliance</span>
        <span className="mt-2 flex gap-2">
          <Bar
            label="Yesterday"
            value={card.compliance.yesterday_pct}
            testId={`odo-yesterday-${card.code}`}
          />
          <Bar label="Today" value={card.compliance.today_pct} testId={`odo-today-${card.code}`} />
          <Bar
            label="Cumulative"
            value={card.compliance.cumulative_pct}
            testId={`odo-cumulative-${card.code}`}
          />
        </span>
      </span>

      {/* the KPI on its band */}
      <span className="block lg:px-6">
        <Bullet kpi={card.kpi} />
      </span>

      {/* target against actual impact */}
      <span className="block lg:px-6" data-testid={`odo-impact-${card.code}`}>
        <span className="label-caps block">Impact</span>
        <span className="mt-1 block w-[150px]">
          <Gauge
            value={card.impact.ratio_pct}
            max={150}
            tone={card.impact.band}
            readout={fmtLakhValue(card.impact.actual_lakh)}
            caption="latest day"
            sub={
              card.impact.target_lakh
                ? `Target ${fmtLakhValue(card.impact.target_lakh)}`
                : "target impact not declared"
            }
            markerLabel={
              card.impact.total_lakh
                ? `${fmtLakhValue(card.impact.total_lakh)} so far`
                : null
            }
            testId={`odo-gauge-${card.code}`}
          />
        </span>
      </span>

      {/* last filled */}
      <span className="block lg:pl-6">
        <span className="label-caps block">Last updated</span>
        <span className="font-data mt-1 flex items-center gap-1.5 text-[13px] font-bold">
          <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
          {card.last_updated ? fmtDay(card.last_updated) : "never"}
        </span>
        {card.last_updated_by && (
          <span className="mt-0.5 block text-[10px] text-muted-foreground">
            by {card.last_updated_by}
          </span>
        )}
        {card.remark && (
          <span className="mt-2 flex items-start gap-1.5 text-[10.5px] leading-snug text-muted-foreground">
            <MessageSquareText className="mt-px h-3 w-3 shrink-0" />
            <span className="line-clamp-3">{card.remark}</span>
          </span>
        )}
        {card.gaps.length > 0 && (
          <span
            className="mt-2 block border border-[hsl(var(--watch))] px-1.5 py-1 font-display text-[9px] font-bold uppercase leading-snug tracking-wider text-[hsl(var(--watch))]"
            data-testid={`odo-gaps-${card.code}`}
          >
            {card.gaps.map((g) => g.label).join(" · ")} still to be declared
          </span>
        )}
      </span>
    </button>
  </div>
);
